import { useCallback, useState } from 'react';
import { AssetRowDescriptor } from '@/app/assets/components/table/hooks/useAssetsTable';
import { RowSelectionState } from '@tanstack/react-table';
import { RawModel } from '@/types/rawModel';
import { ZipWriter, BlobWriter, BlobReader } from '@zip.js/zip.js'; // Adjust import based on your setup
import { RapidModel } from '@/types/rapidModel';
import axios, { AxiosProgressEvent } from 'axios';
import { saveAs } from 'file-saver';
import { DebouncedState } from 'use-debounce';
import {
  UpdateProgressArgs,
  useDownloadProgress,
} from '@/app/assets/components/table/hooks/download/useDownloadProgress';
import { getPBROutputsForDownload } from '@/utils/assetUtils';
import { showErrorNotification } from '@/app/components/shared/notifications';
import { getDownloadUrls } from '../../../../utils/getDownloadUrls';
import { useSingleRawAssetDownload } from '@/app/assets/components/table/columns/assetActionsColumn/hooks/useSingleRawAssetDownload';
import { isCad } from '@/utils/fileUtils';

type DownloadUrl = { name: string; url: string };

type DownloadPackage = {
  name: string;
  downloadFiles: DownloadUrl[];
  outputs: {
    pipelineName: string;
    outputExportName: string;
    downloadFiles: DownloadUrl[];
  }[];
  format: string;
};

/**
 * Hook to manage asset downloads, including zipping multiple assets and their outputs.
 */
export const useAssetsDownload = (rowSelection: RowSelectionState, assets: RawModel[]) => {
  const { progress, updateProgress, resetProgress } = useDownloadProgress();
  const [isPending, setIsPending] = useState(false);
  const { onDownload: onDownloadSingleRawAsset } = useSingleRawAssetDownload();

  const onDownloadAssets = useCallback(async () => {
    const selected = Object.keys(rowSelection).map((key) => JSON.parse(key) as AssetRowDescriptor);

    const allRapidModels = assets.flatMap((a) => a.rapidModels.map((r) => ({ rapidModel: r, rawModel: a })));
    const downloads = await getDownloadPackage(selected, assets, allRapidModels);

    const assetsCount = Object.keys(downloads).length;
    const outputsCount = Object.values(downloads)
      .flatMap((d) => d.outputs)
      .flatMap((i) => i.downloadFiles).length;

    if (assetsCount === 0 && outputsCount === 0) {
      showErrorNotification({
        title: 'No assets selected',
        message: 'Please select assets to download',
      });
      return;
    }

    if (assetsCount === 1 && outputsCount === 0) {
      return onDownloadSingleRawAsset(parseInt(Object.keys(downloads)[0]));
    }

    const totalFiles = (() => {
      const outputsDownloadCount = Object.values(downloads).flatMap((d) =>
        d.outputs.flatMap((o) => o.downloadFiles)
      ).length;
      const assetsDownloadCount = Object.values(downloads).flatMap((d) => d.downloadFiles).length;
      return outputsDownloadCount + assetsDownloadCount;
    })();
    resetProgress(totalFiles);
    await downloadAsZip(setIsPending, downloads, updateProgress);
  }, [rowSelection, assets, resetProgress, updateProgress, onDownloadSingleRawAsset]);

  return { onDownloadAssets, isPending, progress };
};

/**
 * Determines the type and details of an asset based on its descriptor.
 */
function getAsset(
  next: AssetRowDescriptor,
  assets: RawModel[],
  allRapidModels: FlatArray<
    {
      rawModel: RawModel;
      rapidModel: RapidModel;
    }[][],
    1
  >[]
): ['raw' | 'output' | 'postprocessing', { rawModel?: RawModel; rapidModel?: RapidModel; postProcessing?: RawModel }] {
  if (next.type === 'raw') {
    return ['raw', { rawModel: assets.find((a) => a.id === next.id) }];
  }

  const asset = allRapidModels.find((r) => r.rapidModel.id === next.id);

  if (asset?.rapidModel.preset_name === 'DCC Importer') {
    return ['postprocessing', { postProcessing: assets.find((a) => a.id === next.id) }];
  }
  if (!asset) {
    return ['postprocessing', { postProcessing: assets.find((a) => a.id === next.id) }];
  }

  return ['output', asset];
}

/**
 * Prepares a package of assets and their outputs for download.
 */
async function getDownloadPackage(
  selected: AssetRowDescriptor[],
  assets: RawModel[],
  allRapidModels: FlatArray<
    {
      rawModel: RawModel;
      rapidModel: RapidModel;
    }[][],
    1
  >[]
) {
  return selected.sort(assetDescriptorSorter).reduce(
    async (acc, next) => {
      const downloadPackage = await acc;
      let [type, asset] = getAsset(next, assets, allRapidModels);

      if (!asset) {
        return downloadPackage;
      }

      switch (type) {
        case 'raw':
          const rawAsset = asset.rawModel as RawModel;
          const downloadUrls = await getDownloadUrls({ asset: rawAsset });

          createNewPackageItem(rawAsset, downloadPackage, downloadUrls);
          break;
        case 'output':
          const model = asset as {
            rapidModel: RapidModel;
            rawModel: RawModel;
          };

          const assetPackage = ensurePackageItem(model.rawModel, downloadPackage);
          const pipelineOutputs: DownloadPackage['outputs'][number] = {
            pipelineName: getPipelineName(model.rawModel),
            outputExportName: model.rapidModel.export_name || model.rapidModel.name,
            downloadFiles: [],
          };
          assetPackage.outputs.push(pipelineOutputs);

          Object.entries(model.rapidModel.downloads?.all ?? {}).forEach(([extension, url]) => {
            pipelineOutputs.downloadFiles.push({
              url,
              name: `${model.rapidModel.export_name}.${extension}`,
            });
          });
          assetPackage.format = model.rawModel.format;
          break;
        case 'postprocessing': {
          const rawAsset = asset.postProcessing as RawModel;
          const postprocessingPackage = ensurePackageItem(rawAsset, downloadPackage);

          if (next.isMax) {
            const dcc = rawAsset.rapidModels.find((rapid) => rapid.preset_name === 'DCC Importer');

            if (!dcc) {
              throw new Error('DCC Importer not found');
            }

            postprocessingPackage.outputs.push({
              downloadFiles: getPBROutputsForDownload(rawAsset),
              pipelineName: 'DCC Importer',
              outputExportName: '',
            });
          }

          postprocessingPackage.outputs.push({
            downloadFiles: rawAsset.additionalDownloads?.downloadFiles ?? [],
            pipelineName: 'Converted Files',
            outputExportName: '',
          });

          break;
        }
      }

      return acc;
    },
    Promise.resolve({} as Record<string, DownloadPackage>)
  );
}

/**
 * Fetches a file as a blob with progress updates.
 */
async function getBlob(url: string, updateProgress: (progressEvent: AxiosProgressEvent) => void) {
  const response = await axios.get(url, {
    responseType: 'blob',
    onDownloadProgress: updateProgress,
  });
  return response.data;
}

/**
 * Creates a new download package for an asset.
 */
function createNewPackageItem(
  rawAsset: RawModel,
  downloadPackages: Record<string, DownloadPackage>,
  downloadUrls?: DownloadUrl[]
) {
  const newPackage = {
    downloadFiles: downloadUrls ?? [],
    name: rawAsset.name,
    format: rawAsset.format,
    outputs: [] as DownloadPackage['outputs'],
  };
  downloadPackages[rawAsset.id] = newPackage;
  return newPackage;
}

/**
 * Ensures a download package exists for an asset, creating it if necessary.
 */
function ensurePackageItem(rawAsset: RawModel, downloadPackages: Record<string, DownloadPackage>) {
  const existingPackage = downloadPackages[rawAsset.id];
  if (existingPackage) {
    return existingPackage;
  }

  createNewPackageItem(rawAsset, downloadPackages);
  return downloadPackages[rawAsset.id];
}

/**
 * Downloads multiple assets and their outputs as a single zip file using zip.js.
 */
async function downloadAsZip(
  setIsPending: (value: ((prevState: boolean) => boolean) | boolean) => void,
  downloads: Record<string, DownloadPackage>,
  updateProgress: (arg: UpdateProgressArgs, name: string) => void
) {
  setIsPending(true);

  try {
    const blobWriter = new BlobWriter();
    const zipWriter = new ZipWriter(blobWriter);

    // Determine unique root folder names to avoid conflicts
    const rootFolderNames: Record<string, string> = {};
    const usedRootFolderNames = new Set<string>();

    for (const [id, downloadPackage] of Object.entries(downloads)) {
      let candidateName = downloadPackage.name;
      if (usedRootFolderNames.has(candidateName)) {
        candidateName = `${candidateName}_${id}`;
      }
      usedRootFolderNames.add(candidateName);
      rootFolderNames[id] = candidateName;
    }

    // Process each download package
    await Promise.all(
      Object.entries(downloads).map(async ([id, downloadPackage]) => {
        const rootFolderName = rootFolderNames[id];

        // Compute unique workflow folder names for outputs within this package
        const pipelineGroups: Record<string, DownloadPackage['outputs']> = {};
        downloadPackage.outputs.forEach((output) => {
          if (!pipelineGroups[output.pipelineName]) {
            pipelineGroups[output.pipelineName] = [];
          }
          pipelineGroups[output.pipelineName].push(output);
        });

        const outputsNames = new Map<DownloadPackage['outputs'][number], string>();

        for (const pipelineName in pipelineGroups) {
          const outputs = pipelineGroups[pipelineName];
          const usedNames = new Set<string>();
          outputs.forEach((output) => {
            if (!output.outputExportName) {
              outputsNames.set(output, '');
            } else {
              const uniqueName = getUniqueName(output.outputExportName, usedNames);
              usedNames.add(uniqueName);
              outputsNames.set(output, uniqueName);
            }
          });
        }

        // Add asset files
        await Promise.all(
          downloadPackage.downloadFiles.map(async (file) => {
            const blob = await getBlob(file.url, (axiosProgressEvent) =>
              updateProgress({ axiosProgressEvent }, file.url)
            );
            const filePath = `${rootFolderName}/${file.name}`;
            await zipWriter.add(filePath, new BlobReader(blob), {
              onprogress: async (current, total) => {
                updateProgress({ zippingProgress: current / total }, file.url);
              },
            });
            updateProgress({ zippingProgress: 1 }, file.url);
          })
        );

        // Add output files
        await Promise.all(
          downloadPackage.outputs.map(async (output) => {
            const outputFolderName = outputsNames.get(output) || '';
            const basePath = outputFolderName
              ? `${rootFolderName}/${output.pipelineName}/${outputFolderName}`
              : `${rootFolderName}/${output.pipelineName}`;

            await Promise.all(
              output.downloadFiles.map(async (f) => {
                const blob = await getBlob(f.url, (axiosProgressEvent) =>
                  updateProgress({ axiosProgressEvent }, f.url)
                );
                const filePath = `${basePath}/${f.name}`;
                await zipWriter.add(filePath, new BlobReader(blob), {
                  onprogress: async (current, total) => {
                    updateProgress({ zippingProgress: current / total }, f.url);
                  },
                });
                updateProgress({ zippingProgress: 1 }, f.url);
              })
            );
          })
        );
      })
    );

    // Finalize the zip and save it
    await zipWriter.close();
    const zippedBlob = await blobWriter.getData();
    saveAs(zippedBlob, 'download.zip');
  } finally {
    setIsPending(false);
  }
}

/**
 * Determines the pipeline name based on the asset format.
 */
function getPipelineName(rawAsset: RawModel) {
  return rawAsset.format === 'max' ? 'Rapid PBR' : '3D Processor Outputs';
}

/**
 * Generates a unique name by appending an incrementor if necessary.
 */
const getUniqueName = (name: string, usedNames: Set<string>, incrementor = 0): string => {
  let candidate = name;
  if (incrementor > 0) {
    candidate = `${name} (${incrementor})`;
  }
  if (!usedNames.has(candidate)) {
    return candidate;
  }
  return getUniqueName(name, usedNames, incrementor + 1);
};

/**
 * Sorts asset descriptors, prioritizing raw assets.
 */
function assetDescriptorSorter(first: AssetRowDescriptor, second: AssetRowDescriptor) {
  const firstValue = first.type === 'raw' ? 1 : -1;
  const secondValue = second.type === 'raw' ? 1 : -1;

  return firstValue < secondValue ? 1 : -1;
}
